import { useCallback, useEffect, useRef, useState } from 'react';

interface UseSpeechToTextOptions {
  lang?: string;
  onFinalTranscript?: (text: string) => void;
}

export function useSpeechToText(options: UseSpeechToTextOptions = {}) {
  const { lang = 'en-US', onFinalTranscript } = options;

  const [isListening, setIsListening] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState('');
  const [error, setError] = useState<string | null>(null);

  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const onFinalTranscriptRef = useRef(onFinalTranscript);

  useEffect(() => {
    onFinalTranscriptRef.current = onFinalTranscript;
  }, [onFinalTranscript]);

  const SpeechRecognitionClass =
    typeof window !== 'undefined'
      ? window.SpeechRecognition || window.webkitSpeechRecognition
      : null;

  const isSupported = Boolean(SpeechRecognitionClass);

  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch {
        // Ignored if already stopped
      }
    }
  }, []);

  const startListening = useCallback(() => {
    if (!SpeechRecognitionClass) {
      setError('Speech recognition is not supported in this browser.');
      return;
    }

    if (isListening) {
      stopListening();
    }

    setError(null);
    setInterimTranscript('');

    try {
      const recognition = new SpeechRecognitionClass();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = lang;

      recognition.onstart = () => {
        setIsListening(true);
      };

      recognition.onresult = (event: SpeechRecognitionEvent) => {
        let currentInterim = '';
        let finalChunk = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const result = event.results[i];
          const transcriptText = result[0]?.transcript || '';
          if (result.isFinal) {
            finalChunk += transcriptText;
          } else {
            currentInterim += transcriptText;
          }
        }

        setInterimTranscript(currentInterim);

        if (finalChunk.trim()) {
          onFinalTranscriptRef.current?.(finalChunk);
        }
      };

      recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
        const errorType = event.error;
        let errorMessage = 'An error occurred during speech recognition.';

        if (errorType === 'not-allowed' || errorType === 'service-not-allowed') {
          errorMessage = 'Microphone access was denied. Please allow microphone permissions in your browser settings.';
        } else if (errorType === 'no-speech') {
          errorMessage = 'No speech detected. Please speak clearly into your microphone.';
        } else if (errorType === 'network') {
          errorMessage = 'Network connection issue during voice recognition.';
        } else if (errorType === 'audio-capture') {
          errorMessage = 'No microphone was found or microphone hardware is busy.';
        }

        setError(errorMessage);
        setIsListening(false);
        setInterimTranscript('');
      };

      recognition.onend = () => {
        setIsListening(false);
        setInterimTranscript('');
      };

      recognitionRef.current = recognition;
      recognition.start();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Could not start speech recognition.';
      setError(msg);
      setIsListening(false);
    }
  }, [SpeechRecognitionClass, isListening, lang, stopListening]);

  const toggleListening = useCallback(() => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  }, [isListening, startListening, stopListening]);

  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort();
        } catch {
          // Cleanup
        }
      }
    };
  }, []);

  return {
    isListening,
    isSupported,
    interimTranscript,
    error,
    startListening,
    stopListening,
    toggleListening,
  };
}
